import PropTypes from 'prop-types';
import { useApp } from 'hooks';
import DateLine from 'components/ui-kit/date-line';
import PaidSection from 'components/paid-section';
import Money from 'components/ui-kit/money';
import ServicePlanInvoicePayButton from 'components/service-plan-subscription-payments/pay-button';
import { getTipBaseAmount } from 'helpers/calculations.helper';

import * as S from './styles';
import InvoicePayButton from '../invoice-pay-button';

const InvoiceItem = ({
  id,
  title,
  sentDate,
  paidDate,
  totalAmount,
  taxAmount,
  amountDue,
  creditToBeApplied,
  portalId,
  invoiceSettingsOverride,
  invoiceType,
}) => {
  const { isCardPaymentsAvailable } = useApp();
  // What is still owed after any partial payment: the row shows it and its pay button charges it.
  const amountToPay = amountDue ?? totalAmount;
  const tipBaseAmount = getTipBaseAmount(totalAmount, taxAmount);
  const isServicePlanInvoice = invoiceType === 'service-plan';
  const itemPath = isServicePlanInvoice ? `${id}?type=service-plan` : id;

  let paymentAction = null;

  if (!paidDate && isCardPaymentsAvailable) {
    paymentAction = isServicePlanInvoice
      ? (
        <ServicePlanInvoicePayButton
          color="primary"
          invoiceId={id}
          portalId={portalId}
          totalAmount={amountToPay}
          tipBaseAmount={tipBaseAmount}
          invoiceSettingsOverride={invoiceSettingsOverride}
          creditToBeApplied={creditToBeApplied}
        />
      )
      : (
        <InvoicePayButton
          color="primary"
          invoiceId={id}
          portalId={portalId}
          totalAmount={amountToPay}
          tipBaseAmount={tipBaseAmount}
          invoiceSettingsOverride={invoiceSettingsOverride}
          creditToBeApplied={creditToBeApplied}
        />
      );
  }

  return (
    <S.Box to={itemPath} state={{ title, invoiceType }}>
      <S.TypeIcon />
      <S.Title>{title}</S.Title>
      <S.Dates>
        <DateLine date={sentDate} label="Sent Date" />
        <DateLine date={paidDate} dateColor={paidDate ? 'default' : 'blue'} label="Paid Date" />
      </S.Dates>
      <S.Amount>
        <Money amount={amountToPay} />
      </S.Amount>
      <S.Status isPaid={!!paidDate}>
        {paidDate ? <PaidSection /> : paymentAction}
      </S.Status>
    </S.Box>
  );
};

InvoiceItem.propTypes = {
  id: PropTypes.string.isRequired,
  portalId: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  sentDate: PropTypes.string,
  paidDate: PropTypes.string,
  totalAmount: PropTypes.number.isRequired,
  taxAmount: PropTypes.number,
  amountDue: PropTypes.number,
  creditToBeApplied: PropTypes.number,
  invoiceType: PropTypes.oneOf(['job', 'service-plan']),
  invoiceSettingsOverride: PropTypes.shape({
    ccSurchargeOverride: PropTypes.shape({
      enabled: PropTypes.bool.isRequired,
    }),
  }),
};

InvoiceItem.defaultProps = {
  sentDate: null,
  paidDate: null,
  taxAmount: null,
  amountDue: null,
  creditToBeApplied: 0,
  invoiceType: 'job',
  invoiceSettingsOverride: null,
};

export default InvoiceItem;
